import { useEffect, useState, type ReactNode } from 'react';
import {
  adminUsers,
  pulseAlertStorm,
  pulseOncallConfig,
  pulseOncallCurrent,
  pulseOncallOverride,
  pulseOncallSave,
  pulseOncallTest,
  pulseOncallV3,
  pulseRuleAudit,
  pulseRuleEnabled,
  pulseRules,
  pulseSuppressionLift,
  pulseSuppressions,
  type OnCallConfigResponse,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { sx } from '../../ui/designStyles';
import { ConfirmDialog, withConfirm } from '../../ui/ConfirmDialog';
import { chipS, env, idemKey, pre, usePulseApi } from './lib';
import type { Inc } from './SetupIncidents';
import { useCustomerText } from '../../ui/buildLabels';

const CAP = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;';
const BLOCK = 'box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:9px 2px;';
const ROUTE: Record<string, [string, string]> = { page: ['#B3261E', 'rgba(179,38,30,.14)'], ticket: ['#2F5C97', 'rgba(79,138,214,.15)'], unrouted: ['#54606C', 'rgba(34,39,46,.08)'] };
const RSTATE: Record<string, [string, string]> = {
  armed: ['#1F6B52', 'rgba(47,138,111,.16)'],
  flapping: ['#B3261E', 'rgba(179,38,30,.10)'],
  muted: ['#54606C', 'rgba(34,39,46,.08)'],
  'no data': ['#54606C', 'rgba(34,39,46,.08)'],
  'not instrumented': ['#54606C', 'rgba(34,39,46,.08)'],
  surfacing: ['#5B6B7C', 'rgba(91,107,124,.11)'],
  'probe off': ['#54606C', 'rgba(34,39,46,.08)'],
};
const SMALL_BTN = 'display:inline-flex;align-items:center;height:26px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.18);';
const TIER_NAMES = ['Primary on-call', 'Secondary', 'Tertiary / manager'];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * The On-call tab (design puIsAlerts + puIsOnCall, T8134-8333): paging noise, the alert
 * storm, what is suppressed, the rules, then the on-call editor (rotation, ladder,
 * channels, tiers, a test page).
 */
export function OnCallTab({ incidents, canWrite, writeTitle }: { incidents: Inc[]; canWrite: boolean; writeTitle?: string }) {
  const plain = useCustomerText();
  const { client } = useSession();
  const rules = usePulseApi((c, signal) => env(pulseRules(c, { signal })));
  const sups = usePulseApi((c, signal) => env(pulseSuppressions(c, { signal })));
  const storm = usePulseApi((c, signal) => env(pulseAlertStorm(c, { query: { windowMinutes: 5, minIncidents: 4 }, signal })));
  const audit = usePulseApi((c, signal) => env(pulseRuleAudit(c, { signal })));
  const cfg = usePulseApi((c, signal) => env(pulseOncallConfig(c, { signal })));
  const cur = usePulseApi((c, signal) => env(pulseOncallCurrent(c, { signal })));
  const v3 = usePulseApi((c, signal) => pulseOncallV3(c, { signal }));
  const [err, setErr] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ title: string; body: ReactNode; label: string; go: () => Promise<void> } | null>(null);
  const fail = (e: unknown) => setErr(e instanceof Error ? e.message : 'The request failed.');

  // ── Alerts ──
  const R = rules.data ?? [];
  const liveSup = sups.data ?? [];
  const pageRules = R.filter((r) => r.route === 'page');
  const fires = pageRules.reduce((a, r) => a + (r.fires ?? 0), 0);
  const acted = pageRules.reduce((a, r) => a + (r.acted ?? 0), 0);
  const stats: [string, string, string, string][] = [
    ['Pages, recorded window', String(fires), fires === 0 ? '#54606C' : '#22272E', 'across ' + pageRules.length + ' page-routed rules · not a 30-day total'],
    ['Of those, actionable', acted + ' of ' + fires, acted === fires ? '#1F6B52' : '#B3261E', 'the on-call took a specific action'],
    ['Noise', fires - acted + ' pages', fires - acted > 2 ? '#B3261E' : '#54606C', 'each one is a rule to retune, not a fact of life'],
    ['Suppressed right now', String(liveSup.length), liveSup.length ? '#22272E' : '#1F6B52', 'still recording, just not paging'],
  ];
  const threshold = storm.data?.minIncidents ?? 4;
  const liveInc = incidents.filter((i) => i.status !== 'resolved');
  const flapping = liveSup.filter((s) => s.kind === 'flapping');
  const firing = liveInc.length + flapping.length;
  const isStorm = storm.data?.active === true || firing >= threshold;

  // ── On-call editor ──
  const conf = cfg.data;
  const [removed, setRemoved] = useState<Record<string, boolean>>({});
  // "+ add operator": the tier whose picker is open, and who was added per tier (unsaved).
  const [picking, setPicking] = useState<number | null>(null);
  const [added, setAdded] = useState<Record<number, { userId: string; name: string; email: string | null }[]>>({});
  // The Aithora team (GET /admin/users?scope=platform), loaded when a picker first opens.
  const team = usePulseApi((c, signal) => adminUsers(c, { query: { scope: 'platform' }, signal }), [], canWrite && picking !== null);
  const [labels, setLabels] = useState<Record<number, string>>({});
  const [step2, setStep2] = useState<number | null>(null);
  const [crit2, setCrit2] = useState<number | null>(null);
  const [saved, setSaved] = useState(false);
  const [paged, setPaged] = useState(false);
  const [override, setOverride] = useState<OnCallConfigResponse['override'] | undefined>(undefined);
  useEffect(() => {
    if (conf) setOverride(conf.override ?? null);
  }, [conf]);
  const s2 = step2 ?? conf?.ladder.thresholdsMin[1] ?? 15;
  const c2 = crit2 ?? conf?.criticalLadder?.thresholdsMin[1] ?? 5;
  const tiers = [1, 2, 3].map((level) => conf?.tiers.find((t) => t.level === level) ?? { level, userIds: [], members: [] });
  const members = (t: (typeof tiers)[number]) =>
    [...(t.members ?? t.userIds.map((u) => ({ userId: u, name: u, email: null }))), ...(added[t.level] ?? [])].filter((m) => !removed[m.userId]);
  // Who can hold a pager: active Aithora operators, Super Admins and developers.
  const pageable = (team.data?.users ?? []).filter((u) => ['OPERATOR', 'SUPER_ADMIN', 'DEVELOPER'].includes(u.role) && u.status === 'active');
  const roster = members(tiers[0]);
  const start = cur.data?.current?.index ?? 0;
  const rot = (roster.length ? roster : [{ userId: '', name: 'the whole operator team', email: null }]).map((_, i, a) => a[(start + i) % a.length]).slice(0, 3);
  const anchor = conf?.rotation?.anchor ? new Date(conf.rotation.anchor) : null;
  const hhmm = anchor ? String(anchor.getHours()).padStart(2, '0') + ':' + String(anchor.getMinutes()).padStart(2, '0') : '08:00';
  const dayShort = anchor ? DAYS[anchor.getDay()].slice(0, 3) : 'Mon';
  const dayLong = anchor ? DAYS[anchor.getDay()] : 'Monday';
  const weekly = (conf?.rotation?.periodDays ?? 7) === 7;
  const ov = override ?? null;
  const channels = v3.data?.channels;
  const chanRows: [string, { live: boolean | null; reason: string | null; note?: string | null } | undefined][] = [
    ['In-app', channels?.in_app],
    ['Email', channels?.email],
    ['SMS', channels?.sms],
    ['Voice call', channels?.voice],
  ];
  const lastSet = conf?.updatedAt ? 'Last set ' + conf.updatedAt.slice(0, 10) + ' · ' + (conf.updatedByEmail ?? conf.updatedBy ?? 'unknown') : 'Never set: every page goes to the whole operator team.';

  const save = () => {
    if (!canWrite || !conf) return;
    setErr(null);
    pulseOncallSave(client, {
      body: {
        tiers: tiers.map((t, i) => ({ level: t.level, userIds: members(t).map((m) => m.userId), label: labels[i] ?? t.label ?? TIER_NAMES[i] })),
        ladder: { thresholdsMin: [0, s2, s2 * 2] },
        criticalLadder: { thresholdsMin: [0, c2, c2 * 2] },
      },
    }).then(() => {
      setSaved(true);
      cfg.reload();
    }, fail);
  };
  const setOv = (userId: string | null) => {
    if (!canWrite) return;
    pulseOncallOverride(client, { body: { userId } }).then((r) => setOverride(r.data.override ?? null), fail);
  };

  return (
    <>
      <div style={sx('display:flex;flex-direction:column;gap:11px;min-width:0;')} data-testid="pulse-alerts">
        <div style={sx('display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:0 14px;')}>
          {stats.map(([label, val, color, note]) => (
            <div key={label} style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:9px 2px;min-width:0;')}>
              <div style={sx(CAP)}>
                <span>{label}</span>
              </div>
              <div style={sx('font-variant-numeric:tabular-nums;font-size:19px;font-weight:700;line-height:1.1;color:' + color + ';margin-top:7px;')}>
                <span>{rules.data ? val : '–'}</span>
              </div>
              <div style={sx('font-size:14px;color:#54606C;margin-top:5px;line-height:1.45;text-wrap:pretty;')}>
                <span>{note}</span>
              </div>
            </div>
          ))}
        </div>

        <div style={sx(BLOCK)}>
          <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:7px;padding:11px 13px;margin-bottom:11px;border:1px solid rgba(34,39,46,.11);border-radius:9px;background:rgba(34,39,46,.022);')}>
            <span style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:700;white-space:nowrap;')}>Alert storm</span>
            <span style={sx(chipS(isStorm ? '#B3261E' : '#2F5C97', isStorm ? 'rgba(179,38,30,.10)' : 'rgba(79,138,214,.13)'))}>
              <span>{isStorm ? 'STORM' : 'CLEAR'}</span>
            </span>
            <span style={sx('font-size:14px;font-weight:600;min-width:0;')}>
              <span>{isStorm ? firing + ' distinct conditions firing · collapsed into one page' : firing + (firing === 1 ? ' condition' : ' conditions') + ' firing · below the ' + threshold + '-condition storm threshold'}</span>
            </span>
            <span style={sx('flex:1 1 90px;min-width:min(100%,90px);display:flex;align-items:center;gap:8px;')}>
              <span style={sx('flex:1 1 60px;min-width:40px;height:6px;border-radius:3px;background:rgba(34,39,46,.09);overflow:hidden;')}>
                <span style={sx('display:block;height:100%;border-radius:3px;width:' + Math.min(100, Math.round((firing / threshold) * 100)) + '%;background:' + (isStorm ? '#B3261E' : '#2F5C97') + ';opacity:' + (isStorm ? '1' : '.5') + ';')} />
              </span>
              <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;white-space:nowrap;')}>
                <span>{firing + ' of ' + threshold}</span>
              </span>
            </span>
            <span style={sx('flex:1 1 100%;min-width:100%;font-size:14px;color:#54606C;line-height:1.5;text-wrap:pretty;')}>
              <span>
                {'Storm suppression triggers when ' + threshold + ' or more distinct conditions fire inside 5 minutes, sending one page naming the count instead of one per rule. Counted right now: ' +
                  liveInc.length + ' non-resolved incident' + (liveInc.length === 1 ? '' : 's') + ' plus ' + flapping.length + ' flapping rule' + (flapping.length === 1 ? '' : 's') +
                  ' no incident already covers. Maintenance windows are planned and dependency suppressions are their parent incident, so neither is counted. ' +
                  (isStorm ? 'At or above the threshold, so pages collapse into one.' : 'Below the threshold, so pages go out per rule.')}
              </span>
            </span>
          </div>
          <div style={sx(CAP)}>Suppressed right now</div>
          {liveSup.length === 0 && (
            <div style={sx('box-sizing:border-box;background:rgba(34,39,46,.028);border:1px dashed rgba(34,39,46,.18);border-radius:9px;padding:12px 14px;margin-top:10px;')}>
              <div style={sx('font-size:14px;font-weight:700;color:#3C454F;')}>{sups.error ? plain('Suppressions are not served yet') : 'Nothing is suppressed'}</div>
              <div style={sx('font-size:14px;color:#54606C;margin-top:4px;line-height:1.45;text-wrap:pretty;')}>
                {sups.error ? plain('GET /api/pulse/suppressions is not on the back end yet, so this list cannot say what is quiet.') : 'Every armed rule can page right now. Suppression is always temporary and always listed here while it lasts.'}
              </div>
            </div>
          )}
          <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:0 18px;align-items:start;min-width:0;')}>
            {liveSup.map((s) => (
              <div key={s.id} style={sx('display:flex;align-items:flex-start;gap:11px;flex-wrap:wrap;row-gap:7px;padding:11px 0;border-top:1px solid rgba(34,39,46,.07);margin-top:9px;')}>
                <span style={sx('flex:1 1 220px;min-width:min(100%,220px);')}>
                  <span style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:5px;')}>
                    <span style={sx('font-size:16px;font-weight:600;')}>
                      <span>{s.name}</span>
                    </span>
                    <span style={sx(chipS(s.kind === 'flapping' ? '#B3261E' : s.kind === 'maintenance' ? '#2F5C97' : '#54606C', s.kind === 'flapping' ? 'rgba(179,38,30,.10)' : s.kind === 'maintenance' ? 'rgba(79,138,214,.13)' : 'rgba(34,39,46,.07)'))}>
                      <span>{s.kind.toUpperCase()}</span>
                    </span>
                  </span>
                  <span style={sx('display:block;font-size:14px;color:#54606C;margin-top:4px;line-height:1.45;text-wrap:pretty;')}>
                    <span>{s.why}</span>
                  </span>
                </span>
                <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;white-space:nowrap;')}>
                  <span>{s.until}</span>
                </span>
                <a
                  href="#"
                  aria-disabled={!canWrite ? 'true' : undefined}
                  title={!canWrite ? writeTitle : undefined}
                  onClick={(e) => {
                    pre(e);
                    if (canWrite) pulseSuppressionLift(client, { params: { id: s.id } }).then(() => sups.reload(), fail);
                  }}
                  style={sx(SMALL_BTN)}
                >
                  Lift now
                </a>
              </div>
            ))}
          </div>
          <div style={sx('font-size:14px;color:#54606C;margin-top:10px;line-height:1.5;max-width:100ch;text-wrap:pretty;')}>
            <span>Suppression never stops collection. A suppressed rule keeps writing beats to the series, so the series is complete even where the paging was deliberately quiet - and every entry above says when it lifts by itself.</span>
          </div>
        </div>

        <div style={sx(BLOCK)}>
          <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-bottom:9px;')}>Alert rules</div>
          <div>
            <div style={sx('display:grid;grid-template-columns:minmax(0,1.4fr) minmax(0,1.6fr) 74px minmax(0,150px);gap:12px;font-size:14px;letter-spacing:.09em;text-transform:uppercase;white-space:nowrap;color:#54606C;font-weight:600;padding-bottom:7px;border-bottom:1px solid rgba(34,39,46,.14);')}>
              <span>Rule</span>
              <span>Fires when {'→'} clears when</span>
              <span style={sx('text-align:right;')}>Fires</span>
              <span>Routes to</span>
            </div>
            <div style={sx('min-width:0;')}>
              <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(290px,1fr));gap:0 16px;align-items:start;min-width:0;')}>
                {R.map((r) => {
                  const muted = !r.enabled;
                  const st = muted ? 'muted' : r.ruleState ?? 'armed';
                  const route = r.route ?? 'unrouted';
                  const firesTxt = st === 'not instrumented' || st === 'no data' || st === 'probe off' || r.ruleState === 'probe off' || r.ruleState === 'no data' ? '–' : r.ruleState === 'surfacing' ? (r.fires ?? 0) + ' surfaced' : (r.fires ?? 0) + ' / ' + (r.acted ?? 0);
                  return (
                    <div key={r.id} style={sx('display:grid;grid-template-columns:minmax(0,1.4fr) minmax(0,1.6fr) 74px minmax(0,150px);gap:12px;align-items:start;padding:11px 0;border-top:1px solid rgba(34,39,46,.07);')} data-testid="pulse-rule">
                      <span style={sx('min-width:0;')}>
                        <span style={sx('display:block;font-size:16px;font-weight:600;line-height:1.35;text-wrap:pretty;')}>
                          <span>{r.name}</span>
                        </span>
                        <span style={sx('display:flex;align-items:center;gap:6px;flex-wrap:wrap;row-gap:4px;margin-top:5px;')}>
                          <span style={sx(chipS('#54606C', 'rgba(34,39,46,.07)'))}>
                            <span>{(r.golden ?? r.signal).toUpperCase()}</span>
                          </span>
                          <span style={sx(chipS((RSTATE[st] ?? RSTATE.armed)[0], (RSTATE[st] ?? RSTATE.armed)[1]))}>
                            <span>{st.toUpperCase()}</span>
                          </span>
                        </span>
                      </span>
                      <span style={sx('min-width:0;')}>
                        <span style={sx('display:block;font-size:14px;color:#3C454F;line-height:1.5;text-wrap:pretty;')}>
                          <span>{r.firesWhen ?? (r.metric === 'value' ? r.signal + ' ' + (r.comparator ?? '') + ' ' + (r.threshold ?? '') : r.signal + ' is ' + (r.targetStatus ?? 'down'))}</span>
                        </span>
                        <span style={sx('display:block;font-size:14px;color:#54606C;line-height:1.5;margin-top:3px;text-wrap:pretty;')}>
                          {'→'} clears when <span>{r.clearsWhen ?? 'no clear condition yet'}</span> · <span>{r.dampening ?? 'not evaluated'}</span>
                        </span>
                      </span>
                      <span style={sx('font-variant-numeric:tabular-nums;font-size:16px;font-weight:700;text-align:right;letter-spacing:-.01em;color:' + (r.ruleState === 'surfacing' ? '#5B6B7C' : (r.fires ?? 0) > (r.acted ?? 0) ? '#B3261E' : '#54606C') + ';')}>
                        <span>{firesTxt}</span>
                      </span>
                      <span style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;min-width:0;')}>
                        <span style={sx(chipS(ROUTE[route][0], ROUTE[route][1]))}>
                          <span>{route.toUpperCase()}</span>
                        </span>
                        {route !== 'unrouted' && (
                          <a
                            href="#"
                            aria-pressed={muted}
                            aria-disabled={!canWrite ? 'true' : undefined}
                            title={!canWrite ? writeTitle : undefined}
                            onClick={(e) => {
                              pre(e);
                              if (canWrite) pulseRuleEnabled(client, { params: { id: r.id }, body: { enabled: muted } }).then(() => rules.reload(), fail);
                            }}
                            style={sx('display:inline-flex;align-items:center;height:24px;padding:0 10px;border-radius:6px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;' + (muted ? 'background:rgba(34,39,46,.07);color:#54606C;' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.18);'))}
                          >
                            <span>{muted ? 'Unmute' : 'Mute'}</span>
                          </a>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div style={sx('font-size:14px;color:#54606C;margin-top:10px;line-height:1.5;max-width:100ch;text-wrap:pretty;')}>
            <span>Fires reads pages / actionable over the recorded window. Every rule belongs to one of the four golden signals and fires only after the condition holds for the stated dampening window, so a single bad sample never pages. Clearing uses a lower threshold than firing - a signal hovering on the line resolves once, not eleven times.</span>
          </div>
        </div>

        <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:11px;')}>
          {(audit.data ?? []).map((a, n) => (
            <div key={n} style={sx('box-sizing:border-box;background:rgba(34,39,46,.028);border:1px solid rgba(34,39,46,.1);border-radius:11px;padding:9px 2px;min-width:0;')}>
              <div style={sx('font-size:14px;font-weight:700;color:#22272E;line-height:1.4;text-wrap:pretty;')}>
                <span>{a.head}</span>
              </div>
              <div style={sx('font-size:14px;color:#54606C;margin-top:6px;line-height:1.5;text-wrap:pretty;')}>
                <span>{a.body}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:15px 17px;')} data-testid="pulse-oncall">
        <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:7px;')}>
          <span style={sx(CAP)}>On-call &amp; escalation</span>
          <span style={sx('flex:1 1 130px;min-width:min(100%,130px);')} />
          <a
            href="#"
            aria-disabled={!canWrite ? 'true' : undefined}
            title={!canWrite ? writeTitle : undefined}
            onClick={(e) => {
              pre(e);
              save();
            }}
            style={sx('display:inline-flex;align-items:center;height:32px;padding:0 15px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;' + (saved ? 'background:rgba(47,138,111,.14);color:#1F6B52;' : 'background:#2F5C97;color:#fff;'))}
          >
            <span>{saved ? 'Saved' : 'Save'}</span>
          </a>
        </div>
        <div style={sx('font-size:14px;color:#54606C;margin-top:6px;line-height:1.55;max-width:92ch;text-wrap:pretty;')}>
          <span>Who gets paged when an incident opens, and how it escalates if nobody acknowledges. An empty tier pages the whole operator team - never a silent drop. Acknowledging an incident halts escalation.</span>
        </div>
        {err && (
          <div role="alert" style={sx('font-size:14px;color:#B3261E;font-weight:700;margin-top:6px;')}>
            {err}
          </div>
        )}

        <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin:15px 0 4px;')}>Rotation</div>
        <div style={sx('font-size:14px;color:#54606C;line-height:1.5;margin-bottom:6px;max-width:92ch;text-wrap:pretty;')}>
          <span>{'A ' + (weekly ? 'weekly' : 'daily') + ' hand-off at ' + dayLong + ' ' + hhmm + ', rotating through the Tier 1 roster below: same people, one of them holding the pager at a time.'}</span>
        </div>
        {ov && (
          <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:5px;margin-top:9px;padding:9px 11px;border-radius:9px;background:rgba(91,107,124,.09);border:1px solid rgba(91,107,124,.28);min-width:0;')}>
            <span style={sx('flex:1 1 200px;min-width:0;font-size:14px;font-weight:700;color:#5B6B7C;line-height:1.45;text-wrap:pretty;')}>
              <span>{ov.name + ' holds the pager by override: the schedule resumes at the next ' + dayLong + ' hand-off.'}</span>
            </span>
            <a href="#" onClick={(e) => { pre(e); setOv(null); }} style={sx('display:inline-flex;align-items:center;height:26px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.2);')}>
              End override
            </a>
          </div>
        )}
        <div style={sx('min-width:0;')}>
          {rot.map((m, i) => {
            const holds = ov ? m.userId === ov.userId : i === 0;
            const displaced = !!ov && i === 0 && m.userId !== ov.userId;
            return (
              <div key={m.userId + i} style={sx('display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;row-gap:4px;padding:9px 0;border-bottom:1px solid rgba(34,39,46,.06);min-width:0;')}>
                <span style={sx('font-size:14px;color:#54606C;min-width:86px;white-space:nowrap;')}>
                  <span>{i === 0 ? (weekly ? 'This week' : 'Today') : i === 1 ? (weekly ? 'Next week' : 'Tomorrow') : weekly ? 'In two weeks' : 'In two days'}</span>
                </span>
                <span style={sx('font-size:16px;font-weight:700;white-space:nowrap;')}>
                  <span>{m.name}</span>
                </span>
                <span
                  style={sx(
                    'display:inline-flex;align-items:center;height:22px;padding:0 7px;border-radius:4px;font-size:14px;font-weight:700;letter-spacing:.05em;white-space:nowrap;' +
                      (holds ? 'color:#1F6B52;background:rgba(47,138,111,.12);' : displaced ? 'color:#5B6B7C;background:rgba(91,107,124,.13);' : 'color:#54606C;background:rgba(34,39,46,.07);'),
                  )}
                >
                  <span>{holds ? (ov ? 'HOLDS THE PAGER · BY OVERRIDE' : 'HOLDS THE PAGER') : displaced ? 'OVERRIDDEN' : 'SCHEDULED'}</span>
                </span>
                <span style={sx('flex:1 1 90px;min-width:0;font-size:14px;color:#54606C;white-space:nowrap;')}>
                  <span>{dayShort + ' ' + hhmm + ' → ' + dayShort + ' ' + hhmm}</span>
                </span>
                {!holds && m.userId && (
                  <a href="#" aria-disabled={!canWrite ? 'true' : undefined} title={!canWrite ? writeTitle : undefined} onClick={(e) => { pre(e); setOv(m.userId); }} style={sx('display:inline-flex;align-items:center;height:26px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;background:rgba(34,39,46,.05);color:#3C454F;border:1px solid rgba(34,39,46,.14);')}>
                    <span>{'Hand the pager to ' + m.name}</span>
                  </a>
                )}
              </div>
            );
          })}
        </div>

        <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin:15px 0 8px;')}>Escalation ladder</div>
        <div style={sx('display:grid;grid-template-columns:minmax(150px,1.6fr) minmax(90px,1fr) minmax(90px,1fr);gap:10px;font-size:14px;letter-spacing:.09em;text-transform:uppercase;white-space:nowrap;color:#54606C;font-weight:600;padding-bottom:7px;border-bottom:1px solid rgba(34,39,46,.1);')}>
          <span />
          <span>Default</span>
          <span>Critical</span>
        </div>
        <div style={sx('min-width:0;')}>
          <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(290px,1fr));gap:0 16px;align-items:start;min-width:0;')}>
            {[
              ['Tier 1 pages', 'immediately', 'immediately'],
              ['Tier 2 after unacked', s2 + ' min', c2 + ' min'],
              ['Tier 3 after unacked', s2 * 2 + ' min', c2 * 2 + ' min'],
            ].map((l) => (
              <div key={l[0]} style={sx('display:grid;grid-template-columns:minmax(150px,1.6fr) minmax(90px,1fr) minmax(90px,1fr);gap:10px;align-items:center;padding:8px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
                <span style={sx('font-size:14px;color:#3C454F;')}>
                  <span>{l[0]}</span>
                </span>
                <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;')}>
                  <span>{l[1]}</span>
                </span>
                <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;color:#B3261E;')}>
                  <span>{l[2]}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
        <div style={sx('display:flex;align-items:center;gap:12px;flex-wrap:wrap;row-gap:8px;margin-top:10px;')}>
          <span style={sx('display:inline-flex;align-items:center;gap:7px;flex-wrap:wrap;row-gap:5px;')}>
            <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>Tier 2 step</span>
            <input type="text" aria-label="Tier 2 step in minutes" readOnly={!canWrite} value={String(s2)} onChange={(e) => { const n = parseInt(e.target.value, 10); setStep2(isNaN(n) ? 0 : n); setSaved(false); }} style={sx('box-sizing:border-box;width:58px;height:30px;padding:0 9px;border:1px solid rgba(34,39,46,.18);border-radius:7px;background:#FBF8F1;font-variant-numeric:tabular-nums;font-size:14px;color:#22272E;')} />
            <span style={sx('font-size:14px;color:#54606C;')}>min</span>
          </span>
          <span style={sx('display:inline-flex;align-items:center;gap:7px;flex-wrap:wrap;row-gap:5px;')}>
            <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>Critical step</span>
            <input type="text" aria-label="Critical step in minutes" readOnly={!canWrite} value={String(c2)} onChange={(e) => { const n = parseInt(e.target.value, 10); setCrit2(isNaN(n) ? 0 : n); setSaved(false); }} style={sx('box-sizing:border-box;width:58px;height:30px;padding:0 9px;border:1px solid rgba(34,39,46,.18);border-radius:7px;background:#FBF8F1;font-variant-numeric:tabular-nums;font-size:14px;color:#22272E;')} />
            <span style={sx('font-size:14px;color:#54606C;')}>min</span>
          </span>
          <span style={sx('flex:1 1 130px;min-width:min(100%,130px);font-size:14px;color:#54606C;text-wrap:pretty;')}>
            <span>Tier 3 is always twice the Tier 2 step - one number to reason about, not four.</span>
          </span>
        </div>

        <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin:16px 0 6px;')}>Channels a page can reach</div>
        <div style={sx('min-width:0;')}>
          {chanRows.map(([name, ch]) => {
            const live = ch?.live === true;
            return (
              <div key={name} style={sx('display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;row-gap:3px;padding:8px 0;border-bottom:1px solid rgba(34,39,46,.06);min-width:0;')}>
                <span style={sx('font-size:16px;font-weight:700;min-width:84px;white-space:nowrap;')}>
                  <span>{name}</span>
                </span>
                <span style={sx('display:inline-flex;align-items:center;height:22px;padding:0 7px;border-radius:4px;font-size:14px;font-weight:700;letter-spacing:.05em;white-space:nowrap;' + (live ? 'color:#1F6B52;background:rgba(47,138,111,.12);' : 'color:#B3261E;background:rgba(179,38,30,.11);'))}>
                  <span>{ch ? (live ? 'LIVE' : 'NOT PURCHASED') : 'NOT REPORTED'}</span>
                </span>
                <span style={sx('flex:1 1 180px;min-width:0;font-size:14px;color:#54606C;line-height:1.4;text-wrap:pretty;')}>
                  <span>{ch ? ch.note ?? ch.reason ?? '' : 'the back end does not report this channel yet'}</span>
                </span>
              </div>
            );
          })}
        </div>
        {(channels?.sms?.live !== true || channels?.voice?.live !== true) && (
          <div style={sx('font-size:14px;color:#54606C;line-height:1.5;margin-top:7px;max-width:96ch;text-wrap:pretty;')}>
            <span>Until SMS and voice are purchased, every page is in-app and email only: an escalation ladder that ends in a channel nobody is watching is worse than no ladder.</span>
          </div>
        )}

        <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:0 18px;align-items:start;min-width:0;')}>
          {tiers.map((t, ti) => {
            const ms = members(t);
            return (
              <div key={t.level} style={sx('margin-top:14px;padding-top:12px;border-top:1px solid rgba(34,39,46,.1);')}>
                <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:7px;')}>
                  <span style={sx('font-size:14px;font-weight:700;')}>
                    <span>{'Tier ' + t.level + ' · ' + TIER_NAMES[ti]}</span>
                  </span>
                  <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>
                    <span>{ti === 0 ? 'pages immediately' : 'pages at +' + s2 * ti + 'm unacked · critical +' + c2 * ti + 'm'}</span>
                  </span>
                  <span style={sx('flex:1 1 130px;min-width:min(100%,130px);')} />
                  <a
                    href="#"
                    onClick={(e) => { pre(e); if (canWrite) setPicking(picking === t.level ? null : t.level); }}
                    aria-disabled={!canWrite ? 'true' : undefined}
                    aria-expanded={picking === t.level}
                    title={canWrite ? 'Add someone from the Aithora team to this tier' : writeTitle}
                    style={sx('display:inline-flex;align-items:center;height:26px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;background:#FBF8F1;color:#3C454F;border:1px solid rgba(34,39,46,.18);')}>
                    + add operator
                  </a>
                </div>
                {picking === t.level && (
                  <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;margin-top:9px;')}>
                    {team.error ? (
                      <span style={sx('font-size:14px;color:#B3261E;')}>The team list could not be loaded.</span>
                    ) : (
                      <select
                        aria-label={'Add to tier ' + t.level}
                        value=""
                        onChange={(e) => {
                          const u = pageable.find((x) => x.id === e.target.value);
                          if (!u) return;
                          setAdded((o) => ({ ...o, [t.level]: [...(o[t.level] ?? []), { userId: u.id, name: u.name ?? u.email ?? u.id, email: u.email }] }));
                          setRemoved((o) => ({ ...o, [u.id]: false }));
                          setPicking(null);
                          setSaved(false);
                        }}
                        style={sx('box-sizing:border-box;flex:1 1 200px;min-width:min(100%,200px);height:30px;padding:0 9px;border:1px solid rgba(34,39,46,.18);border-radius:7px;background:#FBF8F1;font-size:14px;color:#22272E;')}
                      >
                        <option value="">{team.data ? 'Choose a person' : 'Loading the team'}</option>
                        {pageable.filter((u) => !ms.some((m) => m.userId === u.id)).map((u) => (
                          <option key={u.id} value={u.id}>{(u.name ?? u.email ?? u.id) + (u.email && u.name ? ' · ' + u.email : '')}</option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
                <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:6px;margin-top:9px;')}>
                  <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>Label</span>
                  <input type="text" aria-label={'Tier ' + t.level + ' label'} readOnly={!canWrite} value={labels[ti] ?? t.label ?? TIER_NAMES[ti]} onChange={(e) => { setLabels((o) => ({ ...o, [ti]: e.target.value })); setSaved(false); }} style={sx('box-sizing:border-box;flex:1 1 160px;min-width:min(100%,160px);height:30px;padding:0 10px;border:1px solid rgba(34,39,46,.18);border-radius:7px;background:#FBF8F1;font-size:14px;color:#22272E;')} />
                </div>
                {ms.map((m) => (
                  <div key={m.userId} style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:5px;padding:7px 0;border-top:1px solid rgba(34,39,46,.06);')}>
                    <span style={sx('width:5px;height:5px;border-radius:50%;background:#2F5C97;flex:0 0 5px;')} />
                    <span style={sx('flex:1 1 130px;min-width:min(100%,130px);font-size:14px;')}>
                      <span>{m.name + (m.email ? ' · ' + m.email : '')}</span>
                    </span>
                    <a href="#" aria-disabled={!canWrite ? 'true' : undefined} onClick={(e) => { pre(e); if (canWrite) { setRemoved((o) => ({ ...o, [m.userId]: true })); setSaved(false); } }} style={sx('font-size:14px;color:#B3261E;text-decoration:none;white-space:nowrap;')}>
                      remove
                    </a>
                  </div>
                ))}
                {ms.length === 0 && (
                  <div style={sx('font-size:14px;color:#54606C;margin-top:7px;text-wrap:pretty;')}>
                    <span>none - falls back to the whole operator team, never a silent drop</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={sx('margin-top:14px;padding-top:12px;border-top:1px solid rgba(34,39,46,.1);')}>
          <div style={sx('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:6px;')}>
            <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>On call right now</span>
            <span style={sx('font-size:16px;font-weight:700;')}>
              <span>{ov ? ov.name + ' · by override until the next ' + dayLong + ' hand-off' : roster.length ? rot[0].name + ' · ' + (weekly ? 'this week’s' : 'today’s') + ' rotation' : 'the whole operator team (Tier 1 is empty)'}</span>
            </span>
          </div>
          <div style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:8px;margin-top:10px;')}>
            <span style={sx('font-size:14px;color:#54606C;')}>
              <span>{lastSet}</span>
            </span>
            <span style={sx('flex:1 1 130px;min-width:min(100%,130px);')} />
            <a
              href="#"
              aria-disabled={!canWrite ? 'true' : undefined}
              title={!canWrite ? writeTitle : undefined}
              onClick={(e) => {
                pre(e);
                if (!canWrite) return;
                setConfirm({
                  title: 'Send a test page?',
                  body: 'A real page goes to whoever is on call right now, marked as a test. It tells you today whether the channel is wired.',
                  label: 'Send test page',
                  go: () => pulseOncallTest(client, { body: withConfirm({ idempotency_key: idemKey() }) }).then(() => setPaged(true)),
                });
              }}
              style={sx('display:inline-flex;align-items:center;height:32px;padding:0 14px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.18);')}
            >
              <span>{paged ? 'Test page sent' : 'Send test page'}</span>
            </a>
          </div>
          <div style={sx('font-size:14px;color:#54606C;margin-top:8px;line-height:1.5;max-width:92ch;text-wrap:pretty;')}>
            <span>{paged ? 'A live page went to Tier 1 through the notification queue. Real SMS and voice land when Twilio does.' : 'Sends a real page to the tier on call right now, so you find out today whether the channel is wired.'}</span>
          </div>
          <div style={sx('font-size:14px;color:#54606C;margin-top:4px;line-height:1.5;max-width:92ch;text-wrap:pretty;')}>
            <span>Members are operator and dev users only - a tier cannot reference someone without internal access.</span>
          </div>
        </div>
      </div>
      {confirm && <ConfirmDialog title={confirm.title} body={confirm.body} confirmLabel={confirm.label} onConfirm={confirm.go} onClose={() => setConfirm(null)} />}
    </>
  );
}
